import React, { useState } from "react";
import { Form, Input, Button, Radio, Select, Checkbox } from "antd";
import AppShell from "../../components/app-shell/AppShell";
import useUser from "../../hooks/useUser";
import useData from "../../datas/useData";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";

const AppRegistration = () => {
  const { AppRegistration, sendRegistrationOtp } = useUser();
  const { bloods } = useData();
  const [form] = Form.useForm();
  const [type, setType] = useState("registration");
  const [otpSentFor, setOtpSentFor] = useState("");

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: AppRegistration,
    onSuccess: (res) => {
      toast.success(res?.message);
      form.resetFields();
      setType("registration");
      setOtpSentFor("");
      form.setFieldsValue({ type: "registration", year: "2007" });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || "Registration failed");
    },
  });

  const { mutate: sendOtpAction, isPending: sendingOtp } = useMutation({
    mutationFn: sendRegistrationOtp,
    onSuccess: (res) => {
      toast.success(res?.message || "OTP sent");
      const currentPhone = String(form.getFieldValue("phone") || "").trim();
      setOtpSentFor(currentPhone);
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || error?.response?.data?.error || "Failed to send OTP",
      );
    },
  });

  const handleSendOtp = () => {
    const phone = String(form.getFieldValue("phone") || "").trim();
    if (!phone) {
      toast.error("Enter your phone number first");
      return;
    }
    sendOtpAction({ phone });
  };

  const onFinish = (values) => {
    if (values.type === "check") {
      mutate({
        type: "check",
        phone: String(values.phone || "").trim(),
      });
      return;
    }

    mutate({
      type: "registration",
      name: String(values.name || "").trim(),
      phone: String(values.phone || "").trim(),
      blood: String(values.blood || "").trim(),
      password: values.password,
      otp: String(values.otp || "").trim(),
      year: "2007",
      consent: !!values.consent,
    });
  };

  return (
    <AppShell title="Join 0709" subtitle="SSC 2007 · HSC 2009">
      <p style={{ color: "var(--app-muted)", margin: "0 4px 14px" }}>
        You can use the same phone and password to log in here and in the mobile app.
      </p>
      <div className="app-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          initialValues={{ type: "registration", year: "2007" }}
        >
          <Form.Item label="Choose Type" name="type">
            <Radio.Group
              onChange={(e) => setType(e.target.value)}
              value={type}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio value="registration">Register Account</Radio>
              <Radio value="check">Check Registration</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="Phone"
            name="phone"
            rules={[{ required: true, message: "Phone number is required" }]}
          >
            <Input
              size="large"
              inputMode="tel"
              placeholder="e.g. 01700000000"
              onChange={(e) => {
                const nextPhone = String(e.target.value || "").trim();
                if (otpSentFor && otpSentFor !== nextPhone) {
                  setOtpSentFor("");
                  form.setFieldsValue({ otp: undefined });
                }
              }}
            />
          </Form.Item>

          {type === "registration" && (
            <>
              <Form.Item label="SSC Batch" name="year">
                <Input disabled value="2007" />
              </Form.Item>

              <Form.Item
                label="Full Name"
                name="name"
                rules={[{ required: true, message: "Full name is required" }]}
              >
                <Input size="large" placeholder="Your full name" />
              </Form.Item>

              <Form.Item
                label="Blood Group"
                name="blood"
                rules={[{ required: true, message: "Blood group is required" }]}
              >
                <Select
                  size="large"
                  showSearch
                  placeholder="Select blood group"
                  options={bloods.map((b) => ({ value: b.value, label: b.label }))}
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: "Password is required" },
                  { min: 6, message: "Password must be at least 6 characters" },
                ]}
              >
                <Input.Password size="large" placeholder="Set a password" />
              </Form.Item>

              <div style={{ marginBottom: 12 }}>
                <Button block onClick={handleSendOtp} loading={sendingOtp}>
                  {otpSentFor ? "Resend OTP" : "Send OTP"}
                </Button>
              </div>

              <Form.Item
                label="OTP"
                name="otp"
                rules={[{ required: true, message: "OTP is required" }]}
              >
                <Input
                  size="large"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="Enter OTP sent to your phone"
                />
              </Form.Item>

              <Form.Item
                name="consent"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(new Error("You must agree before registering")),
                  },
                ]}
              >
                <Checkbox>
                  I confirm that everything I share in this app is shared by my own will.
                </Checkbox>
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginTop: 8 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              disabled={isLoading}
              loading={isLoading}
            >
              {isLoading
                ? "Processing..."
                : type === "check"
                  ? "Check Registration"
                  : "Register & Join the Community"}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </AppShell>
  );
};

export default AppRegistration;
